import { useEffect, useRef } from "react"
import moment from "moment"
import { MicVAD } from "@ricky0123/vad-web"
import ProctoringApi from "services/apis/proctoring"
import { storage } from "services/config/storage"
import { useGameplayConfig } from "store"
import { getCurrentQuestion, drainQuestionEvents } from "utils/currentQuestion"

/**
 * ============================================================================
 * [AUDIO PROCTORING v2] Candidate-side audio proctoring
 * ============================================================================
 * Runs ONLY when the position has `audioProctoring` on (isAudioProctored) and an
 * audio-type proctoring session was created at login (storage.userAudioSessionId).
 *
 * On one microphone recording it produces two things, both anchored to a single
 * recording epoch (t = 0 at recording start, §5.4):
 *   1. AUDIO EVIDENCE — a MediaRecorder emits ~15s opus chunks, uploaded to the
 *      unstream `save-audio` endpoint (stored in `media` under the audio session).
 *   2. VAD EVENTS — Silero (MicVAD) detects speech; each finished segment is
 *      buffered as an event with ms offsets on the same clock and a unique
 *      clientEventId, and ridden along with the chunk that closes its window.
 *
 * TIME BASE: offsets are REAL elapsed time from `performance.now()` at start —
 * audio chunk offsets in seconds, VAD event offsets in ms. This makes audio
 * internally exact. Correlation to VIDEO is exact only if the video recorder is
 * also on a real epoch; today video uses an ordinal counter (§8 Q1 — open).
 *
 * SAFETY: every failure mode (mic denied, model load fail, upload error) degrades
 * to a logged no-op and never throws into gameplay. No UI is rendered — this is a
 * pure background hook, invisible to the candidate.
 */

const SAMPLE_RATE = 16000
// VAD runtime assets (Silero model, onnx wasm, worklet). Defaults to the shared
// unberry-vad-assets bucket; override per-env via REACT_APP_VAD_ASSET_PATH — e.g.
// same-origin "/vad/" once the assets are served from the app domain.
const VAD_ASSET_PATH = process.env.REACT_APP_VAD_ASSET_PATH
    || "https://unberry-vad-assets.s3.ap-south-1.amazonaws.com/"
const AUDIO_MIME = "audio/webm;codecs=opus"
const CHUNK_MS = 15000 // §8 Q6 — 15s chunks
// [AUDIO PROCTORING v2] VAD now runs SERVER-side on the stored audio (more reliable,
// tamper-proof, no browser load). The client just records + uploads. Flip to true to
// re-enable the in-browser VAD (e.g. for real-time markers).
const CLIENT_VAD_ENABLED = false

export const useAudioProctoring = () => {
    const { isAudioProctored } = useGameplayConfig()

    const vadRef = useRef(null)
    const recorderRef = useRef(null)
    const streamRef = useRef(null)
    const timerRef = useRef(null)
    const startRef = useRef(0) // performance.now() at recording start — the shared t=0
    const pendingRef = useRef([]) // VAD events not yet shipped
    const lastEndSecRef = useRef(0)
    const tagRef = useRef("")
    const seqRef = useRef(0)

    useEffect(() => {
        if (!isAudioProctored) return undefined

        const sessionId = storage.get.userAudioSessionId()
        if (!sessionId) {
            console.warn("[audio-proctor] audioProctoring on but no audio session id; skipping")
            return undefined
        }

        let cancelled = false
        tagRef.current = `vad-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

        // One complete standalone recording per chunk (start -> stop -> restart), so
        // each uploaded blob is an independently playable webm — mirrors the video
        // recorder. A timeslice would emit non-standalone webm fragments instead.
        const startCycle = () => {
            const rec = recorderRef.current
            if (!rec || cancelled) return
            try {
                rec.start()
                timerRef.current = setTimeout(() => {
                    try { if (rec.state !== "inactive") rec.stop() } catch { /* ignore */ }
                }, CHUNK_MS)
            } catch (err) {
                console.warn("[audio-proctor] recorder start failed:", err?.message)
            }
        }

        const nowMs = () => Math.round(performance.now() - startRef.current)

        // Ship one chunk + the VAD events that ended since the previous chunk.
        const uploadChunk = async (blob) => {
            const endSec = Math.round((performance.now() - startRef.current) / 1000)
            const startSec = lastEndSecRef.current
            lastEndSecRef.current = endSec
            const vadEvents = pendingRef.current
            pendingRef.current = []
            // Question-timeline events since the last chunk — for server-side VAD
            // attribution (Option A). Empty when not in a quiz.
            const questionEvents = drainQuestionEvents()
            const fileName = `audio|${moment().format("DD-MM-YYYY")}|${moment().format("HH:mm:ss")}.webm`
            const file = new File([blob], fileName, { type: AUDIO_MIME })
            try {
                await ProctoringApi.sendUserAudio({
                    file,
                    fileName,
                    startTime: startSec,
                    endTime: endSec,
                    duration: Math.max(0, endSec - startSec),
                    sessionId,
                    vadEvents,
                    questionEvents,
                    // client wall-clock at chunk finalize (same clock as VAD detectedAt)
                    recordedAt: new Date().toISOString(),
                })
            } catch (err) {
                console.warn("[audio-proctor] chunk upload failed:", err?.message)
            }
        }

        const init = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: { deviceId: storage.get.micDevice() },
                })
                if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return }
                streamRef.current = stream
                startRef.current = performance.now() // t = 0

                const mime = MediaRecorder.isTypeSupported(AUDIO_MIME) ? AUDIO_MIME : "audio/webm"
                const recorder = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 16000 })
                recorder.addEventListener("dataavailable", (e) => {
                    if (e.data && e.data.size) uploadChunk(e.data)
                })
                // Restart for the next chunk once each recording finalises.
                recorder.addEventListener("stop", () => { if (!cancelled) startCycle() })
                recorderRef.current = recorder
                startCycle() // begin the first ~15s chunk

                if (CLIENT_VAD_ENABLED) {
                    const vad = await MicVAD.new({
                        model: "v5",
                        baseAssetPath: VAD_ASSET_PATH,
                        onnxWASMBasePath: VAD_ASSET_PATH,
                        positiveSpeechThreshold: 0.3,
                        negativeSpeechThreshold: 0.2,
                        minSpeechMs: 250,
                        redemptionMs: 500,
                        startOnLoad: false,
                        onSpeechEnd: (audio) => {
                            const durationMs = Math.round(((audio?.length ?? 0) / SAMPLE_RATE) * 1000)
                            const endTime = nowMs()
                            const { questionId, questionType, index } = getCurrentQuestion()
                            pendingRef.current.push({
                                clientEventId: `${tagRef.current}-${seqRef.current++}`,
                                event: "speech",
                                startTime: Math.max(0, endTime - durationMs),
                                endTime,
                                durationMs,
                                detectedAt: new Date().toISOString(),
                                questionId,
                                questionType,
                                questionIndex: index,
                            })
                        },
                    })
                    if (cancelled) { vad.destroy(); return }
                    vadRef.current = vad
                    await vad.start()
                }
            } catch (err) {
                console.warn("[audio-proctor] init failed — audio proctoring off this session:", err?.message)
            }
        }

        init()

        return () => {
            cancelled = true
            clearTimeout(timerRef.current)
            try { if (recorderRef.current?.state !== "inactive") recorderRef.current?.stop() } catch { /* ignore */ }
            try { vadRef.current?.destroy() } catch { /* ignore */ }
            try { streamRef.current?.getTracks().forEach((t) => t.stop()) } catch { /* ignore */ }
            recorderRef.current = null
            vadRef.current = null
            streamRef.current = null
        }
    }, [isAudioProctored])
}
